import { useState, useEffect } from 'react';
import { TestTube2, FileText, Clock, CheckCircle } from 'lucide-react';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { LabSample, LabReport, LabTracking } from '../types';
import Spinner from '../../../components/ui/Spinner';

// The stages the real lab-booking backend actually moves through — the
// stepper must only show states a booking can genuinely reach.
const STATUS_STEPS = ['BOOKED', 'SAMPLE_COLLECTED', 'PROCESSING', 'REPORT_READY', 'COMPLETED'];

// Backend LAB_BOOKING_STATUS → stepper stage.
const STATUS_TO_STEP: Record<string, string> = {
  REQUESTED: 'BOOKED',
  ACCEPTED: 'BOOKED',
  SCHEDULED: 'BOOKED',
  BOOKED: 'BOOKED',
  SAMPLE_COLLECTED: 'SAMPLE_COLLECTED',
  IN_PROGRESS: 'PROCESSING',
  PROCESSING: 'PROCESSING',
  REPORT_READY: 'REPORT_READY',
  COMPLETED: 'COMPLETED',
};

export default function LabTracking() {
  const [tab, setTab] = useState<'samples' | 'reports'>('samples');
  const [samples, setSamples] = useState<LabSample[]>([]);
  // The LAB's own progress on its orders. The stepper used to be drawn from a
  // booking's status, and nothing in the system ever advances that — the real
  // stages and their timestamps live on the lab's order.
  const [tracking, setTracking] = useState<LabTracking[]>([]);
  const [reports, setReports] = useState<LabReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(false);
    const p = tab === 'samples'
      ? Promise.all([
          patientApi.getLabSamples().then(data => { if (alive) setSamples(data); }),
          // Supporting detail: a booking still lists without the lab's progress.
          patientApi.getLabTracking()
            .then(data => { if (alive) setTracking(data); })
            .catch(() => { if (alive) setTracking([]); }),
        ]).then(() => undefined)
      : patientApi.getLabReports().then(data => { if (alive) setReports(data); });
    p.catch(() => { if (alive) setLoadError(true); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tab]);

  return (
    <div className="space-y-6" style={govPageContainer()}>
      <div>
        <h1 style={GOV_TYPE.pageTitle}>Lab Tracking</h1>
        <p className="text-sm text-gray-500 mt-1">Track your lab samples and view reports</p>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {(['samples', 'reports'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'samples' ? 'Sample Tracking' : 'Reports'}
          </button>
        ))}
      </div>

      {loadError && !loading && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
          Could not load your lab data from the server. Please refresh the page.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : tab === 'samples' ? (
        samples.length === 0 ? (
          <div className="text-center py-12">
            <TestTube2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No lab samples found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {samples.map(sample => {
              const isCancelled = sample.status === 'CANCELLED';
              // The lab's own order for these tests, when it has one. Matched
              // by test name because a patient booking and a lab order are
              // separate records — nothing links them today.
              // By id when the lab's order carries the booking it came from;
              // by test name only for an order raised before that link
              // existed, or one the lab entered at its own front desk.
              const lab = tracking.find(t => t.patientBookingId === sample.id)
                ?? tracking.find(t =>
                  !t.patientBookingId
                  && (t.testNames.some(n => sample.tests.includes(n))
                    || t.testNames.join(', ') === sample.testName));
              // The lab's stage beats the booking's status: the booking's only
              // ever moves when the patient books or cancels.
              const stepIndex = STATUS_STEPS.indexOf(
                (lab ? STATUS_TO_STEP[lab.stage] ?? lab.stage : STATUS_TO_STEP[sample.status]) ?? 'BOOKED');
              const stageAt: Record<string, string | null | undefined> = {
                BOOKED: lab?.placedAt,
                SAMPLE_COLLECTED: lab?.sampleCollectedAt,
                PROCESSING: lab?.receivedAt ?? lab?.verifiedAt,
                REPORT_READY: lab?.releasedAt,
                COMPLETED: lab?.releasedAt,
              };
              return (
                <div key={sample.id} className="card p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{sample.testName}</p>
                      <p className="text-xs text-gray-500">{sample.labName} · {sample.sampleCode}</p>
                    </div>
                    <LabStatusBadge status={sample.status} />
                  </div>

                  {/* Status Stepper — hidden for cancelled bookings */}
                  <div className="relative" style={isCancelled ? { display: 'none' } : undefined}>
                    <div className="flex items-center justify-between">
                      {STATUS_STEPS.map((step, i) => {
                        const done = i <= stepIndex;
                        const current = i === stepIndex;
                        return (
                          <div key={step} className="flex flex-col items-center relative z-10" style={{ width: `${100 / STATUS_STEPS.length}%` }}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                              done ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-400'
                            } ${current ? 'ring-2 ring-teal-300' : ''}`}>
                              {done ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                            </div>
                            <span className={`text-[9px] mt-1 text-center leading-tight ${done ? 'text-teal-700 font-medium' : 'text-gray-400'}`}>
                              {step.replace(/_/g, '\n')}
                            </span>
                            {/* The date the LAB stamped this stage. Absent when
                                the order has not reached it — never a guess. */}
                            {stageAt[step] && (
                              <span className="text-[8px] text-gray-400 mt-0.5">
                                {new Date(stageAt[step] as string).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="absolute top-3 left-0 right-0 h-0.5 bg-gray-200 -z-0">
                      <div className="h-full bg-teal-600 transition-all" style={{ width: `${(stepIndex / (STATUS_STEPS.length - 1)) * 100}%` }} />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Booked: {new Date(sample.orderedAt).toLocaleDateString('en-IN')}</span>
                    {/* When the sample is actually due. `collectedAt` was read
                        here and this endpoint has never sent it, so the one
                        date a tracking page exists for was never shown. */}
                    {sample.scheduledDate && <span>Sample due: {new Date(sample.scheduledDate).toLocaleDateString('en-IN')}</span>}
                    {sample.referringDoctorName && <span>Ordered by {sample.referringDoctorName}</span>}
                  </div>
                  {sample.notes && <p className="text-xs text-gray-500 mt-1">{sample.notes}</p>}

                  {lab && (
                    <div className="text-[11px] text-gray-400">
                      Lab order {lab.orderNumber}
                      {lab.reportReady && lab.reportNumber ? ` · report ${lab.reportNumber} ready` : ''}
                    </div>
                  )}

                  {/* The one thing on this page a patient may have to act on. */}
                  {lab?.stage === 'RECOLLECTION_REQUIRED' && (
                    <div className="text-[11px] leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      The lab needs another sample for this order. Contact {lab.labName} to arrange it.
                    </div>
                  )}
                  <PaymentBadge payment={(sample as any).paymentStatus || (sample as any).paymentMode} />
                </div>
              );
            })}
          </div>
        )
      ) : (
        reports.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No lab reports yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(report => (
              <div key={report.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{report.testName}</p>
                      <p className="text-xs text-gray-500">{report.labName} · {new Date(report.reportDate).toLocaleDateString('en-IN')}</p>
                    </div>
                  </div>
                  {/* The LAB's own flag. This card used to show nothing at
                      all here, because `isCritical` was hardcoded false — a
                      result the lab had marked critical reached the patient
                      looking like any other. */}
                  {report.isCritical && <span className="badge-red text-[10px] shrink-0">Critical</span>}
                </div>

                {report.isCritical && (
                  <div className="mt-2 text-[11px] leading-relaxed text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {report.criticalCount > 1
                      ? `${report.criticalCount} findings on this report were flagged critical by the lab.`
                      : 'A finding on this report was flagged critical by the lab.'}
                    {' '}Your doctor has been notified. Contact them if you have not heard.
                  </div>
                )}

                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-gray-400">
                  {report.reportNumber && <span className="font-mono">{report.reportNumber}</span>}
                  {report.orderNumber && <span className="font-mono">{report.orderNumber}</span>}
                  {report.status && <span>{report.status.toLowerCase()}</span>}
                  {report.sharedAt && <span>Shared {new Date(report.sharedAt).toLocaleDateString('en-IN')}</span>}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function PaymentBadge({ payment }: { payment?: string }) {
  if (!payment) return null;
  const cfg: Record<string, { bg: string; text: string; label: string }> = {
    PAID: { bg: 'bg-green-100', text: 'text-green-700', label: '✓ Paid' },
    ONLINE: { bg: 'bg-teal-100', text: 'text-teal-700', label: '✓ Paid' },
    PAY_AT_LAB: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Pay at Lab' },
    PAY_AT_VISIT: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Pay at Visit' },
    PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Payment Pending' },
    FAILED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Payment Failed' },
  };
  const c = cfg[payment] || { bg: 'bg-gray-100', text: 'text-gray-600', label: payment.replace(/_/g, ' ') };
  return (
    <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>
      <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{c.label}</span>
    </div>
  );
}

function LabStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    REQUESTED: 'badge-blue', ACCEPTED: 'badge-blue', SCHEDULED: 'badge-blue', BOOKED: 'badge-blue',
    SAMPLE_COLLECTED: 'badge-yellow', IN_PROGRESS: 'badge-yellow', PROCESSING: 'badge-yellow',
    REPORT_READY: 'badge-green', COMPLETED: 'badge-green', CANCELLED: 'badge-red',
  };
  return <span className={`${colors[status] || 'badge-gray'} text-[10px]`}>{status.replace(/_/g, ' ')}</span>;
}
